import type { LiveQuote } from '@/stores/quotes';

/**
 * 用实时行情算单笔持仓的「现价」与「浮动盈亏」。
 *
 * 现价(浮盈用的平仓参考价):
 *   - BUY 持仓用 bid(以卖出价平多)
 *   - SELL 持仓用 ask(以买入价平空)
 *
 * 浮动盈亏(XAUUSD 口径,与 App 端 orders_screen 完全一致):
 *   diff = BUY ? (现价 - 开仓价) : (开仓价 - 现价)
 *   pnl  = diff × volume
 *   volume 是 centi-lot(0.01 手)。XAUUSD 合约为 100 盎司/手,盎司数 == volume,
 *   故 (价差) × volume 即等于美元浮盈。
 *
 * ⚠️ 该简化口径仅对 XAUUSD(账户存款货币 USD、合约 100)精确;系统当前仅开放 XAUUSD。
 *    若将来上多品种,需按各品种 contractSize + 报价货币→存款货币汇率换算
 *    (后端持仓端点需补 contractSize / quoteCcy 等字段,见契约维护)。
 */
export function computePositionLive(
  pos: { side: 'BUY' | 'SELL'; volume: number; entryPrice: number },
  quote: Pick<LiveQuote, 'bid' | 'ask'> | undefined,
): { currentPrice?: number; unrealizedPnl?: number } {
  if (!quote) return {};
  const isBuy = pos.side === 'BUY';
  const currentPrice = isBuy ? quote.bid : quote.ask;
  const diff = isBuy ? currentPrice - pos.entryPrice : pos.entryPrice - currentPrice;
  return { currentPrice, unrealizedPnl: diff * pos.volume };
}

/**
 * 净盈亏 = 毛浮动盈亏(unrealizedPnl)+ 库存费(swap)+ 佣金(commission)。
 *
 * 上面的 `computePositionLive` 只算**毛价差**浮盈,不含任何费用;cTrader 客户端里看到的
 * 「净盈亏」(netUnrealizedPnL)= 毛浮盈 + 库存费 + 已记佣金(开仓腿;**不含**平仓佣金)。
 * 故二者天然有差,差额正是 swap + commission。
 *
 * swap / commission 由后端 `/main-account/positions` 直接给出,单位是**美元**且带 cTrader 符号
 * (成本为负),因此直接相加即可,无需再缩放。口径与后端已实现盈亏 `net = pnl + swap + commission`
 * (见 `execution/handler.rs`)、账户级 `equity = balance + Σ netUnrealizedPnL` 完全一致。
 *
 * `grossPnl` 为 undefined(行情未到、毛浮盈不可算)时返回 undefined,UI 同毛浮盈一起显示「实时」。
 */
export function computeNetPnl(
  grossPnl: number | undefined,
  swap?: number,
  commission?: number,
): number | undefined {
  if (grossPnl == null) return undefined;
  return grossPnl + (swap ?? 0) + (commission ?? 0);
}
